"use client";

import { GoogleTagManager } from "@next/third-parties/google";
import { Analytics } from "@vercel/analytics/react";
import { AnimatePresence, motion } from "motion/react";
import Link from "next/link";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { CONSENT_CHANGE_EVENT, CONSENT_STATUSES, getStoredConsent, updateGtagConsent } from "@/lib/consent";

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

export default function Tracking() {
  const [consent, setConsent] = useState(undefined);
  const [reopened, setReopened] = useState(false);

  useEffect(() => {
    setConsent(getStoredConsent());
  }, []);

  useEffect(() => {
    function handleReopen() {
      setReopened(true);
    }

    window.addEventListener(CONSENT_CHANGE_EVENT, handleReopen);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handleReopen);
  }, []);

  function handleChoice(status) {
    setConsent(status);
    setReopened(false);
    updateGtagConsent(status);
  }

  // undefined until mounted, so the banner never renders server-side
  const showBanner = Boolean(GTM_ID) && (consent === null || reopened);

  return (
    <>
      {GTM_ID ? <GoogleTagManager gtmId={GTM_ID} /> : null}
      <Analytics />
      <AnimatePresence>
        {showBanner ? (
          <motion.aside
            role="dialog"
            aria-label="Cookie consent"
            className="fixed bottom-4 right-4 left-4 z-50 border border-border bg-white p-5 shadow-xl sm:left-auto sm:max-w-92"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-sm text-foreground-light">
              We use cookies to understand how this site is used. Nothing is stored unless you accept — see our{" "}
              <Link href="/legal/privacy-policy-web" className="text-primary underline underline-offset-2 hover:text-primary-600">
                Privacy Policy
              </Link>
              .
            </p>
            <div className="mt-4 flex items-center gap-4">
              <Button variant="default" size="xs" className="px-4" onClick={() => handleChoice(CONSENT_STATUSES.GRANTED)}>
                Accept all
              </Button>
              <Button variant="link-secondary" size="xs" onClick={() => handleChoice(CONSENT_STATUSES.DENIED)}>
                Reject all
              </Button>
            </div>
          </motion.aside>
        ) : null}
      </AnimatePresence>
    </>
  );
}
